import React from 'react';
import { Link, useLocation } from 'react-router';
import styled from 'styled-components';
import { PATHS } from 'shared/consts/paths';
import { getSeoPageMeta } from 'shared/consts/seoClusters';

const Nav = styled.nav`
    width: 100%;
    max-width: 860px;
    margin: 0 0 24px;
    font-size: 13px;
    line-height: 20px;
    color: #6b7280;
`;

const OrderedList = styled.ol`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
    padding: 0;
    margin: 0;
    list-style: none;
`;

const Item = styled.li`
    display: inline-flex;
    align-items: center;

    &:not(:last-child)::after {
        content: '/';
        margin: 0 8px;
        color: #9ca3af;
    }
`;

const CrumbLink = styled(Link)`
    color: #6b7280;
    text-decoration: none;
    &:hover {
        color: #1268fb;
        text-decoration: underline;
    }
`;

const Current = styled.span`
    color: #111827;
    font-weight: 500;
`;

interface CrumbItem {
    label: string;
    path?: string;
}

interface BreadcrumbsProps {
    /** Optional explicit items. If omitted, the component derives breadcrumbs from the current route. */
    items?: CrumbItem[];
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
    const location = useLocation();
    const trail = items ?? deriveTrail(location.pathname);

    if (!trail || trail.length === 0) return null;

    return (
        <Nav aria-label="Breadcrumb">
            <OrderedList>
                {trail.map((crumb, i) => {
                    const isLast = i === trail.length - 1;

                    return (
                        <Item key={`${crumb.label}-${i}`}>
                            {isLast || !crumb.path ? (
                                <Current aria-current="page">{crumb.label}</Current>
                            ) : (
                                <CrumbLink to={crumb.path}>{crumb.label}</CrumbLink>
                            )}
                        </Item>
                    );
                })}
            </OrderedList>
        </Nav>
    );
};

/**
 * Derive Home > Page from the current pathname for known SEO routes.
 * Returns an empty array if the current route is not a mapped SEO page
 * (so consumers can decide to render nothing).
 */
const deriveTrail = (pathname: string): CrumbItem[] => {
    const meta = getSeoPageMeta(pathname);

    if (!meta) return [];

    return [{ label: 'Home', path: PATHS.HOME }, { label: 'Resources', path: PATHS.RESOURCES }, { label: meta.label }];
};

export default Breadcrumbs;
